import { openDB, type DBSchema, type IDBPDatabase } from "idb";

// Database schema version - increment when schema changes
const DB_VERSION = 1;
const DB_NAME = "kablan_offline_db";

// Sync status for offline items
export type SyncStatus = "pending" | "syncing" | "synced" | "error";

// Offline daily log structure
export interface OfflineDailyLog {
  id: string;
  projectId: string;
  date: string;
  weatherTemp: number | null;
  weatherConditions: string | null;
  weatherAuto: boolean;
  crewCount: number | null;
  workPerformed: string;
  issues: string | null;
  createdAt: string;
  syncStatus: SyncStatus;
  syncError?: string;
  lastSyncAttempt?: string;
  retryCount: number;
}

// Offline photo structure
export interface OfflinePhoto {
  id: string;
  dailyLogId: string;
  projectId: string;
  blob: Blob;
  filename: string;
  caption?: string;
  createdAt: string;
  syncStatus: SyncStatus;
  syncError?: string;
  lastSyncAttempt?: string;
  retryCount: number;
}

// Sync metadata for tracking overall sync state
export interface SyncMetadata {
  key: string;
  lastSyncTime?: string;
  pendingCount: number;
  lastError?: string;
}

// IndexedDB schema definition
interface KablanDBSchema extends DBSchema {
  pending_daily_logs: {
    key: string;
    value: OfflineDailyLog;
    indexes: {
      "by-project": string;
      "by-status": SyncStatus;
      "by-date": string;
    };
  };
  pending_photos: {
    key: string;
    value: OfflinePhoto;
    indexes: {
      "by-daily-log": string;
      "by-project": string;
      "by-status": SyncStatus;
    };
  };
  sync_metadata: {
    key: string;
    value: SyncMetadata;
  };
}

let dbInstance: IDBPDatabase<KablanDBSchema> | null = null;

/**
 * Initialize and get the IndexedDB database instance
 */
export async function getDB(): Promise<IDBPDatabase<KablanDBSchema>> {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<KablanDBSchema>(DB_NAME, DB_VERSION, {
    upgrade(db, _oldVersion, _newVersion) {
      // Create stores if they don't exist
      if (!db.objectStoreNames.contains("pending_daily_logs")) {
        const logStore = db.createObjectStore("pending_daily_logs", {
          keyPath: "id",
        });
        logStore.createIndex("by-project", "projectId");
        logStore.createIndex("by-status", "syncStatus");
        logStore.createIndex("by-date", "date");
      }

      if (!db.objectStoreNames.contains("pending_photos")) {
        const photoStore = db.createObjectStore("pending_photos", {
          keyPath: "id",
        });
        photoStore.createIndex("by-daily-log", "dailyLogId");
        photoStore.createIndex("by-project", "projectId");
        photoStore.createIndex("by-status", "syncStatus");
      }

      if (!db.objectStoreNames.contains("sync_metadata")) {
        db.createObjectStore("sync_metadata", { keyPath: "key" });
      }
    },
  });

  return dbInstance;
}

// ============================================
// Daily Log Operations
// ============================================

/**
 * Save a daily log to IndexedDB for offline storage
 */
export async function saveDailyLogOffline(log: Omit<OfflineDailyLog, "syncStatus" | "retryCount">): Promise<OfflineDailyLog> {
  const db = await getDB();
  const offlineLog: OfflineDailyLog = {
    ...log,
    syncStatus: "pending",
    retryCount: 0,
  };
  await db.put("pending_daily_logs", offlineLog);
  await updateSyncMetadata();
  return offlineLog;
}

/**
 * Get all pending daily logs (not yet synced)
 */
export async function getPendingDailyLogs(): Promise<OfflineDailyLog[]> {
  const db = await getDB();
  return db.getAllFromIndex("pending_daily_logs", "by-status", "pending");
}

/**
 * Get all pending daily logs for a specific project
 */
export async function getPendingDailyLogsByProject(projectId: string): Promise<OfflineDailyLog[]> {
  const db = await getDB();
  const allLogs = await db.getAllFromIndex("pending_daily_logs", "by-project", projectId);
  return allLogs.filter((log) => log.syncStatus === "pending" || log.syncStatus === "error");
}

/**
 * Get a specific daily log by ID
 */
export async function getDailyLogById(id: string): Promise<OfflineDailyLog | undefined> {
  const db = await getDB();
  return db.get("pending_daily_logs", id);
}

/**
 * Update the sync status of a daily log
 */
export async function updateDailyLogSyncStatus(
  id: string,
  status: SyncStatus,
  error?: string
): Promise<void> {
  const db = await getDB();
  const log = await db.get("pending_daily_logs", id);
  if (log) {
    log.syncStatus = status;
    log.lastSyncAttempt = new Date().toISOString();
    if (error) {
      log.syncError = error;
      log.retryCount += 1;
    } else if (status === "synced") {
      log.syncError = undefined;
    }
    await db.put("pending_daily_logs", log);
    await updateSyncMetadata();
  }
}

/**
 * Remove a daily log from offline storage (after successful sync)
 */
export async function removeSyncedDailyLog(id: string): Promise<void> {
  const db = await getDB();
  await db.delete("pending_daily_logs", id);
  await updateSyncMetadata();
}

/**
 * Get all daily logs (pending and error) that need sync
 */
export async function getLogsToSync(): Promise<OfflineDailyLog[]> {
  const db = await getDB();
  const allLogs = await db.getAll("pending_daily_logs");
  return allLogs.filter(
    (log) => log.syncStatus === "pending" || (log.syncStatus === "error" && log.retryCount < 3)
  );
}

// ============================================
// Photo Operations
// ============================================

/**
 * Save a photo to IndexedDB for offline storage
 */
export async function savePhotoOffline(photo: Omit<OfflinePhoto, "syncStatus" | "retryCount">): Promise<OfflinePhoto> {
  const db = await getDB();
  const offlinePhoto: OfflinePhoto = {
    ...photo,
    syncStatus: "pending",
    retryCount: 0,
  };
  await db.put("pending_photos", offlinePhoto);
  return offlinePhoto;
}

/**
 * Get all pending photos for a daily log
 */
export async function getPendingPhotosByDailyLog(dailyLogId: string): Promise<OfflinePhoto[]> {
  const db = await getDB();
  return db.getAllFromIndex("pending_photos", "by-daily-log", dailyLogId);
}

/**
 * Get all pending photos (not yet synced)
 */
export async function getPendingPhotos(): Promise<OfflinePhoto[]> {
  const db = await getDB();
  return db.getAllFromIndex("pending_photos", "by-status", "pending");
}

/**
 * Update photo sync status
 */
export async function updatePhotoSyncStatus(
  id: string,
  status: SyncStatus,
  error?: string
): Promise<void> {
  const db = await getDB();
  const photo = await db.get("pending_photos", id);
  if (photo) {
    photo.syncStatus = status;
    photo.lastSyncAttempt = new Date().toISOString();
    if (error) {
      photo.syncError = error;
      photo.retryCount += 1;
    } else if (status === "synced") {
      photo.syncError = undefined;
    }
    await db.put("pending_photos", photo);
  }
}

/**
 * Remove a synced photo from offline storage
 */
export async function removeSyncedPhoto(id: string): Promise<void> {
  const db = await getDB();
  await db.delete("pending_photos", id);
}

/**
 * Get photos that need sync
 */
export async function getPhotosToSync(): Promise<OfflinePhoto[]> {
  const db = await getDB();
  const allPhotos = await db.getAll("pending_photos");
  return allPhotos.filter(
    (photo) => photo.syncStatus === "pending" || (photo.syncStatus === "error" && photo.retryCount < 3)
  );
}

// ============================================
// Sync Metadata Operations
// ============================================

/**
 * Update sync metadata with current pending counts
 */
async function updateSyncMetadata(): Promise<void> {
  const db = await getDB();
  const pendingLogs = await getPendingDailyLogs();
  const pendingPhotos = await getPendingPhotos();

  await db.put("sync_metadata", {
    key: "global",
    lastSyncTime: new Date().toISOString(),
    pendingCount: pendingLogs.length + pendingPhotos.length,
  });
}

/**
 * Get current sync metadata
 */
export async function getSyncMetadata(): Promise<SyncMetadata | undefined> {
  const db = await getDB();
  return db.get("sync_metadata", "global");
}

/**
 * Get total count of pending items
 */
export async function getPendingCount(): Promise<number> {
  const db = await getDB();
  const logs = await db.getAllFromIndex("pending_daily_logs", "by-status", "pending");
  const photos = await db.getAllFromIndex("pending_photos", "by-status", "pending");
  return logs.length + photos.length;
}

/**
 * Clear all offline data (use with caution!)
 */
export async function clearAllOfflineData(): Promise<void> {
  const db = await getDB();
  await db.clear("pending_daily_logs");
  await db.clear("pending_photos");
  await db.clear("sync_metadata");
}

// ============================================
// Utility Functions
// ============================================

/**
 * Generate a UUID for offline items
 */
export function generateOfflineId(): string {
  return `offline_${crypto.randomUUID()}`;
}

/**
 * Check if an ID is an offline-generated ID
 */
export function isOfflineId(id: string): boolean {
  return id.startsWith("offline_");
}

/**
 * Check if IndexedDB is available
 */
export function isIndexedDBAvailable(): boolean {
  try {
    return typeof indexedDB !== "undefined";
  } catch {
    return false;
  }
}
